import { Logger, TLogger } from 'components/logger/Logger';
import { StateManager } from 'components/stateManager/StateManager';
import {
  IVideoControllerComponentsCommonProps,
  IVideoElementControllerProps,
} from 'components/videoElementController/IVideoElementController';
import {
  EVideoElementControllerBusEvent,
  TVideoControllerEventBus,
} from 'components/videoElementController/VideoElementController.events';
import { IPlayerState } from 'types/common';
import { bind } from 'utils/bind';

let countCheckingAutoPlay = 0;

export class AutoplayController {
  private _config: IVideoElementControllerProps = {};
  private _logger: TLogger;
  private _eventsMap = {
    [EVideoElementControllerBusEvent.CAN_PLAY]: this._checkAutoplayEnabled,
    [EVideoElementControllerBusEvent.DESTROY]: this.destroy,
    [EVideoElementControllerBusEvent.FORCE_PLAY_AFTER_PAUSE]: this._forcePlayAfterPause,
    [EVideoElementControllerBusEvent.RESET_CHECKING_AUTOPLAY_COUNT]:
      this._resetCheckingAutoplayCount,
  };

  private readonly _state: StateManager<IPlayerState>;
  private readonly _eventBus: TVideoControllerEventBus;

  constructor(props: IVideoControllerComponentsCommonProps) {
    this._state = props.state;
    this._eventBus = props.eventBus;
    this._logger = Logger(this._eventBus, 'VideoElementController - AutoplayController');

    this._logger.info('Created');
    this._logger.debug('Initial config', this._config);
    this._logger.debug('Initial state', this._state);

    this.init();
  }

  public setConfig(config: IVideoElementControllerProps): void {
    this._logger.debug('Config update received, the new one:', config);
    this._config = config;
  }

  @bind
  public destroy() {
    this._logger.info('Destroying');
    this._eventBus.toggleSubscriptionBulk({
      action: 'unsubscribe',
      callbacks: this._eventsMap,
    });
  }

  @bind
  public init() {
    this._logger.info('Initialization requested, attaching eventBus listeners');
    this._eventBus.toggleSubscriptionBulk({
      action: 'subscribe',
      callbacks: this._eventsMap,
    });
  }

  public _handleConfigUpdated(): void {}

  // Play/pause behaviour
  @bind
  private _forcePlayAfterPause(data: { reason?: string }) {
    if (this._config.autoplay !== undefined && !this._config.autoplay) {
      this._logger.verbose(`Autoplay is disabled.`);
      return;
    }

    this._logger.verbose(`Trying to force video to play.
    Reason: ${data.reason}`);
    const state = this._state.getState();

    if (!state.startedPlaying || state.isPlaying || this._config.canBePaused) {
      this._logger.verbose(`Currently unable to autoplay the video`);
      return;
    }

    this._checkAutoplayEnabled();
  }

  @bind
  private _checkAutoplayEnabled() {
    this._logger.debug('Checking if autoplay is possible');
    if (!this._state.getState().canPlay) {
      this._logger.verbose('No canPlay event caught, skipping check');
      return;
    }

    if (this._config.autoplay !== undefined && !this._config.autoplay) {
      this._logger.verbose(`Autoplay is disabled.`);
      return;
    }

    if (countCheckingAutoPlay > (this._config.limitCheckingAutoplay as number)) {
      this._logger.verbose(
        `Unable to autoplay the video. The limit of autoplay checking (${this._config.limitCheckingAutoplay}) is exceeded`,
      );
      this._eventBus.publish(EVideoElementControllerBusEvent.PAUSED, {});

      return;
    }

    countCheckingAutoPlay += 1;
    this._logger.verbose('Autoplay is used to play the video');
    this._eventBus.publish(EVideoElementControllerBusEvent.VIDEO_START, {});
  }

  @bind
  private _resetCheckingAutoplayCount() {
    countCheckingAutoPlay = 0;
  }
}
